import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { KioskSlot } from './hooks';

const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildDateOptions(weekOffset: number) {
  const out: { date: string; dayName: string; dayNum: number; monthName: string; isToday: boolean }[] = [];
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now); d.setDate(d.getDate() + weekOffset + i);
    const absoluteOffset = weekOffset + i;
    out.push({
      date: d.toISOString().split('T')[0],
      dayName: absoluteOffset === 0 ? 'Today' : absoluteOffset === 1 ? 'Tmrw' : DAY_NAMES[d.getDay()],
      dayNum: d.getDate(), monthName: MONTH_SHORT[d.getMonth()], isToday: absoluteOffset === 0,
    });
  }
  return out;
}

function formatTime(t: string): string {
  const [hh, mm] = t.split(':').map(Number);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  return `${hh % 12 || 12}:${mm.toString().padStart(2, '0')} ${ampm}`;
}

function getPeriod(t: string): 'morning' | 'afternoon' | 'evening' {
  const h = parseInt(t.split(':')[0], 10);
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
}

interface Props {
  selectedDate: string;
  onDateChange: (date: string) => void;
  slots: KioskSlot[];
  closed: boolean;
  loading: boolean;
  accentColor: string;
  headingFont: string;
  onSelectTime: (time: string) => void;
}

export function KioskDateTimePicker({ selectedDate, onDateChange, slots, closed, loading, accentColor, headingFont, onSelectTime }: Props) {
  const [weekOffset, setWeekOffset] = useState(0);
  const dates = useMemo(() => buildDateOptions(weekOffset), [weekOffset]);
  const canGoPrev = weekOffset > 0;

  const grouped = useMemo(() => {
    const g: Record<string, KioskSlot[]> = { morning: [], afternoon: [], evening: [] };
    for (const s of slots) g[getPeriod(s.start)].push(s);
    return g;
  }, [slots]);

  return (
    <div style={{ padding: '0 24px' }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1A1A1A', fontFamily: headingFont, margin: '28px 0 16px' }}>
        When works for you?
      </h2>

      {/* Date pills with week chevrons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 16 }}>
        <button
          type="button"
          aria-label="Previous week"
          onClick={() => canGoPrev && setWeekOffset(o => Math.max(0, o - 7))}
          disabled={!canGoPrev}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 56, borderRadius: 10, flexShrink: 0,
            background: '#fff', border: '1px solid #E5E5EA',
            cursor: canGoPrev ? 'pointer' : 'not-allowed',
            color: canGoPrev ? '#1A1A1A' : '#D1D1D6', padding: 0,
          }}
        >
          <ChevronLeft size={18} />
        </button>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', flex: 1, scrollbarWidth: 'none' }}>
          {dates.map(d => {
            const sel = d.date === selectedDate;
            return (
              <button key={d.date} onClick={() => onDateChange(d.date)} style={{
                background: sel ? accentColor : '#fff',
                color: sel ? '#fff' : '#1A1A1A',
                border: sel ? `2px solid ${accentColor}` : d.isToday ? `2px solid ${accentColor}40` : '1px solid #E5E5EA',
                borderRadius: 14, padding: '10px 12px', cursor: 'pointer',
                fontFamily: BODY_FONT, textAlign: 'center', minWidth: 58, flexShrink: 0,
                transition: 'all 200ms ease',
              }}>
                <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.75 }}>{d.dayName}</div>
                <div style={{ fontSize: 20, fontWeight: 600, marginTop: 1 }}>{d.dayNum}</div>
                <div style={{ fontSize: 10, opacity: 0.6 }}>{d.monthName}</div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          aria-label="Next week"
          onClick={() => setWeekOffset(o => o + 7)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 56, borderRadius: 10, flexShrink: 0,
            background: '#fff', border: '1px solid #E5E5EA',
            cursor: 'pointer', color: '#1A1A1A', padding: 0,
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Slots */}
      {loading ? (
        <div style={{ padding: '48px 0', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', width: 28, height: 28, borderRadius: 14, border: '2.5px solid #E5E5EA', borderTopColor: accentColor, animation: 'kiosk-spin 600ms linear infinite' }} />
          <style>{`@keyframes kiosk-spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      ) : closed ? (
        <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: BODY_FONT }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#636366' }}>Closed on this day</div>
          <div style={{ fontSize: 13, color: '#ADB5BD', marginTop: 6 }}>Try another date above</div>
        </div>
      ) : slots.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: BODY_FONT }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#636366' }}>No openings on this date</div>
          <div style={{ fontSize: 13, color: '#ADB5BD', marginTop: 6 }}>Try another day</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 24 }}>
          {(['morning', 'afternoon', 'evening'] as const).map(period => {
            const ps = grouped[period];
            if (ps.length === 0) return null;
            return (
              <div key={period}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#ADB5BD', fontFamily: BODY_FONT, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  {{ morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' }[period]}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {ps.map(slot => (
                    <button key={slot.start} onClick={() => onSelectTime(slot.start)} style={{
                      background: '#fff', border: '1px solid #E5E5EA', borderRadius: 10,
                      padding: '11px 0', cursor: 'pointer', fontFamily: BODY_FONT,
                      fontSize: 14, fontWeight: 500, color: '#1A1A1A', textAlign: 'center',
                      transition: 'all 200ms ease',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.background = `${accentColor}0A`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E5EA'; e.currentTarget.style.background = '#fff'; }}
                    >
                      {formatTime(slot.start)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
